import properties from '../../modules/ajio-util/properties';

const imageBaseUrl = properties.getProperty('imageBaseUrl');

/**
 * Currency icons for the earn widget. These two are NOT part of
 * centralLoyaltyConfig.icons — the flag only carries the RelianceOne mark and
 * the tier badges — so they stay repo assets served off imageBaseUrl, the same
 * way every other in-app icon is handled (see OrderSummary, DeliverySLAList).
 *
 * The existing ROne_Points.svg / superCash.svg are the refund-breakup icons and
 * are left untouched so those surfaces keep their current artwork.
 */
export const RONE_POINTS_ICON = `${imageBaseUrl}/static/img/ROne_Points_Loyalty.svg`;
export const SUPER_CASH_ICON = `${imageBaseUrl}/static/img/superCash_Loyalty.svg`;

/**
 * `tiers` key for "not enrolled" (its configured cohort is `cl_tier_none`).
 * Treated as no tier (BAU) on every surface.
 */
export const NO_TIER = 'NONE';

export const RELIANCE_ONE_WALLET = 'RELIANCE_ONE_WALLET';
export const AJIO_CASH_WALLET = 'AJIO_CASH_WALLET';

type CentralLoyaltyConfig = {
  tiers?: Record<string, string>;
  icons?: Record<string, string>;
  account?: { title?: string; loyaltyProgramUrl?: string };
};

/**
 * Account ingress destination: configured program URL + the tier as received.
 * e.g. /c/static/relianceone-loyalty-program?loyalty_tier=cl_tier_gold
 * Returns '' when either the URL or the tier is missing, so callers can skip
 * the row rather than link nowhere.
 */
export const getLoyaltyProgramHref = (
  centralLoyaltyConfig: CentralLoyaltyConfig = {},
  tier = ''
): string => {
  const { loyaltyProgramUrl = '' } = centralLoyaltyConfig?.account || {};
  if (!loyaltyProgramUrl || !tier) return '';
  return `${loyaltyProgramUrl}?loyalty_tier=${tier}`;
};

/** AJIO / LUXE get the blue RelianceOne mark, AJIOGRAM the white one. */
export const getRoneLogo = (
  centralLoyaltyConfig: CentralLoyaltyConfig = {},
  store = '', 
  isAccount = false,
): string => {
  const { relianceBlue = '', relianceWhite = '' } = centralLoyaltyConfig?.icons || {};
  return String(store).toLowerCase() === 'ajiogram' && isAccount ? relianceWhite : relianceBlue;
};

/**
 * Tier badge artwork for the tier as received.
 *
 * This is the ONLY place the `tiers` map is read: the received cohort is matched
 * against its values to find the key that names the icon (cl_tier_gold -> GOLD
 * -> icons.goldDarkBadge). The key is never used as the tier value.
 *
 * Dark artwork everywhere except the AJIOGRAM account row, which passes 'light'.
 */
export const getTierBadgeIcon = (
  centralLoyaltyConfig: CentralLoyaltyConfig = {},
  tier = '',
  context: 'light' | 'dark' = 'dark'
): string => {
  if (!tier) return '';
  const { tiers = {}, icons = {} } = centralLoyaltyConfig || {};
  const tierKey = Object.keys(tiers).find(key => tiers[key] === tier);
  if (!tierKey) return '';
  const iconKey = `${tierKey.toLowerCase()}${context === 'light' ? 'Light' : 'Dark'}Badge`;
  return icons[iconKey] || '';
};
